import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";
import Reveal from "@/components/base/Reveal";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <section id="categories" className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl">
            {t("categories.title")}
          </h2>
          <p className="text-foreground-600 mt-4 leading-relaxed">
            {t("categories.description")}
          </p>
        </div>
      </Reveal>

      <Reveal>
        <div className="max-w-xl mx-auto mb-12">
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 rounded-lg bg-background-100 border border-background-200 text-foreground-800 cursor-pointer transition-colors hover:border-primary-300"
            >
              <span className="flex items-center gap-2 text-sm md:text-base font-medium">
                <i className="ri-layout-grid-line text-primary-500 text-lg"></i>
                {t("dropdown.label")}
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${open ? "rotate-180" : ""}`}></i>
            </button>

            {open && (
              <div className="absolute left-0 right-0 mt-2 rounded-lg bg-background-50 border border-background-200 z-40 max-h-80 overflow-y-auto">
                {categories.map((c) => (
                  <a
                    key={c.id}
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <img
                      src={c.image}
                      alt={t(c.nameKey)}
                      className="w-8 h-8 rounded-md object-cover object-top"
                    />
                    {t(c.nameKey)}
                  </a>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-foreground-500 mt-3 text-center">
            {t("dropdown.placeholder")}
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 md:gap-5">
        {categories.map((c, i) => (
          <Reveal key={c.id} delay={(i % 7) * 40}>
            <a
              href={c.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
            >
              <div className="w-full h-32 md:h-36 overflow-hidden">
                <img
                  src={c.image}
                  alt={t(c.nameKey)}
                  title={`${t(c.nameKey)} — USFans Spreadsheet`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="flex items-center justify-between gap-2 px-3 py-3">
                <span className="text-sm font-medium text-foreground-800 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                  {t(c.nameKey)}
                </span>
                <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0">
                  <i className="ri-arrow-right-up-line text-sm group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
                </span>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}